import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Link2, Check, Wrench } from 'lucide-react';

/**
 * Header pubblico condiviso da `/changelog`, `/roadmap` e (col flag
 * `hideShare`) dalla sezione autenticata `/assistance`.
 *
 * Mostra il logo StoreOps a sinistra + tabs "Changelog" / "Backlog" /
 * "Assistenza" subito dopo (la tab attiva è quella corrispondente al
 * pathname). A destra, slot `actions` per RSS / Condividi / azioni
 * specifiche di pagina (es. "Nuovo ticket" in Assistenza).
 *
 * NB: il tab "Assistenza" punta a una pagina autenticata. Se un visitatore
 * non loggato lo clicca, `ProtectedRoute` lo redirige a /login. Comportamento
 * voluto: comunica che esiste una sezione interna senza esporne i dati.
 */
const TABS = [
  { to: '/changelog', label: 'Changelog' },
  { to: '/roadmap',   label: 'Backlog' },
  { to: '/assistance', label: 'Assistenza', icon: Wrench },
];

export default function PublicHeader({ actions, copyEnabled = true }) {
  const { pathname } = useLocation();
  const [copied, setCopied] = React.useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href.replace(/#.*$/, ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* permission denied */ }
  };

  return (
    <header
      className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-20 app-safe-top"
      data-testid="public-header"
    >
      <div className="max-w-6xl mx-auto px-3 sm:px-6 h-14 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 transition-colors mr-1 sm:mr-2 shrink-0" data-testid="public-home-link">
            <div className="w-7 h-7 bg-black flex items-center justify-center shrink-0">
              <span className="text-white font-heading font-bold text-xs">S</span>
            </div>
            <span className="font-heading font-bold tracking-tight text-slate-900 hidden sm:inline">StoreOps</span>
          </Link>
          <span className="text-slate-300 mx-1 hidden sm:inline">·</span>
          <nav className="flex items-center gap-0.5 sm:gap-1 min-w-0 overflow-x-auto scrollbar-hide" data-testid="public-tabs">
            {TABS.map(t => {
              const active = pathname === t.to || pathname.startsWith(t.to + '/');
              const Icon = t.icon;
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  className={`px-2 sm:px-2.5 py-1 text-sm transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
                    active
                      ? 'text-slate-900 font-semibold'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                  data-testid={`public-tab-${t.to.slice(1)}`}
                >
                  {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
                  {t.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {actions}
          {copyEnabled && (
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 px-2 sm:px-2.5 py-1 border border-slate-200 hover:border-slate-300 transition-colors"
              data-testid="public-share-btn"
              aria-label="Condividi link"
              title="Condividi link"
            >
              {copied
                ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> <span className="hidden sm:inline">Copiato</span></>
                : <><Link2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Condividi</span></>}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
